'use client';

import { MapPin, Zap, Award } from 'lucide-react';
import { PublicSection } from '../../../../components/public/PublicSection';
import { PROJECT_NAME } from '@shared/constants';
import { LandingPageCard } from '../../landing/components';

/**
 * @group Public
 * @summary Brand promise section with three pillars
 */
export function BrandPromiseSection() {
  const pillars = [
    {
      icon: MapPin,
      title: 'Access',
      description: `Healthcare should never depend on where you live or how far you can travel. ${PROJECT_NAME} breaks down geographical barriers, bringing fulltime employed, registered HPCSA doctors to anyone with a mobile phone connection. Whether you're a taxi driver working long hours in Johannesburg, a farmworker in the Eastern Cape, or a busy professional in Durban, quality medical care is equally accessible to all South Africans.`,
    },
    {
      icon: Zap,
      title: 'Efficiency',
      description: 'Your time matters. Our intelligent triage system asks the right questions to understand your symptoms, then matches you with the most appropriate doctor — all within minutes, not hours. No more taking a full day off work to sit in a waiting room. No more missing important family time. Healthcare that respects your schedule and gets you back to living well, faster.',
    },
    {
      icon: Award,
      title: 'Quality',
      description: `Every doctor on ${PROJECT_NAME} is a fulltime employee, fully registered with the Health Professions Council of South Africa (HPCSA), and follows established clinical protocols. Our fulltime employed practitioners are not contractors or part-time consultants — they are dedicated team members committed to providing consistent, quality care. Our AI technology supports clinicians by providing preliminary information, but never replaces their professional judgement. You receive the same standard of care you'd expect in a face-to-face consultation — with the added convenience of digital health records and follow-up reminders.`,
    },
  ];

  return (
    <PublicSection 
      heading="Our Brand Promise: Access, Efficiency, and Quality"
      subheading={`${PROJECT_NAME} was built on three fundamental pillars that guide everything we do. These aren't just values on a website — they're commitments we make to every patient, every day, in every consultation across South Africa.`}
      headingColor="#084349"
      subheadingColor="#084349"
      separatorColor="#084349"
      separatorLineColor="hsl(var(--brand-teal) / 0.4)"
      className="bg-hero-bg"
      indented={false}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {pillars.map((pillar, idx) => (
          <LandingPageCard
            key={idx}
            icon={pillar.icon}
            title={pillar.title}
            description={pillar.description}
            iconBg="bg-secondary"
            iconSize="w-8 h-8"
            iconPadding="p-4"
            className="p-8"
          />
        ))}
      </div>
    </PublicSection>
  );
}
